import { useState } from "react";
import { Layers } from "lucide-react";
import { saveCompanyServiceCategories, type PrimeCompany, type ServiceCategory } from "../api/CrmApi";
import PhraseChipsInput, { withPhrases } from "./PhraseChipsInput";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";

interface Props {
  company: PrimeCompany;
  onClose: () => void;
  onSaved?: (company: PrimeCompany) => void;
  /** The list changed under the form (409): reload the company and close, so reopening starts from the current list. */
  onStale?: () => void;
}

/** The Edit service categories form: only what the company does, one phrase per chip. Saving counts as research
 *  when there is at least one phrase you typed or confirmed, or when you tick "researched, nothing found". */
export default function EditServiceCategoriesModal({ company, onClose, onSaved, onStale }: Props) {
  const [items, setItems] = useState<ServiceCategory[]>(company.service_categories ?? []);
  const [draft, setDraft] = useState("");
  const [researchedNone, setResearchedNone] = useState(company.service_basis === "reviewed_none");
  // The list the form was opened on, frozen: the save is refused if it changed since (a parent refresh while the
  // form is open must not silently move this).
  const [base] = useState<ServiceCategory[]>(company.service_categories ?? []);
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    setStale(false);
    try {
      // Whatever is typed in the box but not yet a chip counts too.
      const final = withPhrases(items, draft);
      const updated = await saveCompanyServiceCategories(company.id, final, researchedNone && final.length === 0, base);
      onSaved?.(updated);
      onClose();
    } catch (err: any) {
      setStale(err?.response?.status === 409);
      setError(err?.response?.data?.detail || err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title="Edit service categories"
      description={company.name || company.domain || "company"}
      icon={<Layers />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-[11px] text-muted-foreground">
          What this company does, one phrase per chip (paste a comma list to split it). The fit check for an RFP reads these
          phrases; only phrases you typed or confirmed can rule a company out. Phrases from the CollabP sheet are shown muted:
          confirm the ones that are right with the check mark. Removing sheet phrases without adding your own brings them
          back on the next sheet sync — tick &ldquo;Researched — nothing found&rdquo; to stop that.
        </p>
        <PhraseChipsInput value={items} onChange={setItems} draft={draft} onDraftChange={setDraft} disabled={saving} />
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={researchedNone}
            disabled={saving || items.length > 0 || draft.trim() !== ""}
            onChange={(e) => setResearchedNone(e.target.checked)}
            className="accent-primary"
          />
          Researched — nothing found
        </label>
      </div>
      {error && (
        <Alert tone="danger" className="mt-4">
          {error}
          {stale && onStale && (
            <button type="button" onClick={onStale} className="ml-2 font-medium underline">
              Reload latest
            </button>
          )}
        </Alert>
      )}
    </Modal>
  );
}
